import React, { useEffect, useMemo, useState } from 'react';
import type { Layout, ResponsiveLayouts } from 'react-grid-layout/react';
import { ResponsiveGridLayout, noCompactor, useContainerWidth } from 'react-grid-layout/react';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import useAppRegistry from '../../hooks/useAppRegistry';
import useDashboardBoard from '../../hooks/useDashboardBoard';
import useModuleState from '../../hooks/useModuleState';
import { AppModuleId } from '../../types';
import {
  BOARD_LAYOUT_GAP_PX,
  BOARD_LAYOUT_ROOT_COLUMN_COUNT,
  BOARD_LAYOUT_ROW_UNIT_PX,
} from '../../utils/dashboardBoardLayout';
import { serializeDashboardGridLayoutItems } from '../../utils/appRegistry';
import ModuleCard from './ModuleCard';
import { resetDashboardModuleState } from './DashboardModules';
import TabsDrawer from './TabsDrawer';

interface DashboardBoardProps {
  isAdmin: boolean;
}

const BREAKPOINTS = {
  xl: 1280,
  md: 768,
  sm: 0,
} as const;

const COLS = {
  xl: BOARD_LAYOUT_ROOT_COLUMN_COUNT,
  md: 4,
  sm: 1,
} as const;

type BoardBreakpoint = keyof typeof COLS;

interface DashboardModuleErrorBoundaryProps {
  children: React.ReactNode;
  fallback: (error: Error) => React.ReactNode;
}

interface DashboardModuleErrorBoundaryState {
  error: Error | null;
}

class ErrorBoundary extends React.Component<DashboardModuleErrorBoundaryProps, DashboardModuleErrorBoundaryState> {
  state: DashboardModuleErrorBoundaryState = {
    error: null,
  };

  static getDerivedStateFromError(error: Error): DashboardModuleErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error) {
    console.error('Dashboard module render failed:', error);
  }

  componentDidUpdate(prevProps: DashboardModuleErrorBoundaryProps) {
    if (prevProps.children !== this.props.children && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return this.props.fallback(this.state.error);
    }

    return this.props.children;
  }
}

function areGridItemsEqual(left: Layout, right: Layout) {
  const normalizedLeft = serializeDashboardGridLayoutItems(left);
  const normalizedRight = serializeDashboardGridLayoutItems(right);

  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }

  return normalizedLeft.every((item, index) => {
    const target = normalizedRight[index];
    return (
      item.i === target?.i &&
      item.x === target?.x &&
      item.y === target?.y &&
      item.w === target?.w &&
      item.h === target?.h
    );
  });
}

function cloneGridLayoutItems(items: Layout = []): Layout {
  return items.map((item) => ({ ...item }));
}

function buildResponsiveLayouts(xlLayout: Layout, currentLayouts?: ResponsiveLayouts): ResponsiveLayouts {
  return {
    ...(currentLayouts ?? {}),
    xl: cloneGridLayoutItems(xlLayout),
  };
}

const DashboardBoard: React.FC<DashboardBoardProps> = ({ isAdmin }) => {
  const { isHydrated, layout, placedModuleIds, addModule, removeModule, setModuleColor, saveGridLayoutXl, resetBoard } = useDashboardBoard();
  const { registryById, availableModules, placedModules } = useAppRegistry(placedModuleIds);
  const [searchQuery, setSearchQuery] = useState('');
  const [moduleRetryKeys, setModuleRetryKeys] = useState<Record<string, number>>({});
  const { width, mounted, containerRef } = useContainerWidth({ initialWidth: 1440 });
  const { state: drawerState, saveState: saveDrawerState } = useModuleState(
    'tabs-drawer',
    { isCollapsed: false },
    { storageKey: 'crypto-missions:tabsDrawerState' }
  );

  const placementIds = useMemo(
    () => layout.order.filter((placementId) => Boolean(layout.placements[placementId]?.visible)),
    [layout.order, layout.placements]
  );

  const activeBreakpoint = useMemo<BoardBreakpoint>(() => {
    if (width >= BREAKPOINTS.xl) {
      return 'xl';
    }

    if (width >= BREAKPOINTS.md) {
      return 'md';
    }

    return 'sm';
  }, [width]);

  const isDesktopEditable = isAdmin && activeBreakpoint === 'xl';

  const currentXlLayout = useMemo<Layout>(
    () => cloneGridLayoutItems(layout.gridLayouts?.xl ?? []),
    [layout.gridLayouts]
  );
  const [responsiveLayouts, setResponsiveLayouts] = useState<ResponsiveLayouts>(() =>
    buildResponsiveLayouts(currentXlLayout)
  );

  useEffect(() => {
    setResponsiveLayouts((current) => {
      if (areGridItemsEqual(current.xl ?? [], currentXlLayout)) {
        return current;
      }

      return buildResponsiveLayouts(currentXlLayout);
    });
  }, [currentXlLayout]);

  const handleToggleDrawer = async () => {
    await saveDrawerState((current) => ({
      ...current,
      isCollapsed: !current.isCollapsed,
    }));
  };

  const persistGridLayout = async (currentLayout: Layout) => {
    if (!isDesktopEditable) {
      return;
    }

    if (areGridItemsEqual(currentLayout, currentXlLayout)) {
      return;
    }

    const visiblePlacementIds = new Set(placementIds);
    const nextLayout = currentLayout
      .filter((item) => visiblePlacementIds.has(item.i))
      .map((item) => ({
        i: item.i,
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h,
      }));

    await saveGridLayoutXl(nextLayout);
  };

  const retryModule = (placementId: string) => {
    setModuleRetryKeys((current) => ({
      ...current,
      [placementId]: (current[placementId] ?? 0) + 1,
    }));
  };

  const resetModule = async (placementId: string, moduleId: AppModuleId) => {
    await resetDashboardModuleState(moduleId);
    retryModule(placementId);
  };

  return (
    <div className="grid items-start gap-3 xl:grid-cols-[minmax(0,1fr)_20rem]">
      <div
        ref={containerRef}
        className="dashboard-grid-dots min-w-0 rounded-3xl py-1"
        style={{ minHeight: 'var(--dashboard-min-panel-height, 180px)' }}
      >
        {mounted && isHydrated ? (
          <ResponsiveGridLayout
            className="dashboard-react-grid-layout"
            width={width}
            layouts={responsiveLayouts}
            breakpoints={BREAKPOINTS}
            cols={COLS}
            rowHeight={BOARD_LAYOUT_ROW_UNIT_PX}
            margin={[BOARD_LAYOUT_GAP_PX, BOARD_LAYOUT_GAP_PX]}
            containerPadding={[0, 0]}
            compactor={noCompactor}
            dragConfig={{
              enabled: isDesktopEditable,
              bounded: false,
              handle: '.dashboard-module-drag-handle',
              cancel: '.dashboard-module-controls,.dashboard-module-controls *,.dashboard-no-drag,.dashboard-no-drag *,[data-no-drag="true"],[data-no-drag="true"] *,button,input,textarea,select,a',
              threshold: 4,
            }}
            resizeConfig={{
              enabled: isDesktopEditable,
            }}
            onLayoutChange={(_, nextLayouts) => {
              setResponsiveLayouts(buildResponsiveLayouts(nextLayouts.xl ?? currentXlLayout, nextLayouts));
            }}
            onDragStop={(currentLayout) => void persistGridLayout(currentLayout)}
            onResizeStop={(currentLayout) => void persistGridLayout(currentLayout)}
          >
            {placementIds.map((placementId) => {
              const placement = layout.placements[placementId];
              if (!placement?.visible) {
                return null;
              }

              const definition = registryById[placement.moduleId as AppModuleId];
              if (!definition) {
                return null;
              }

              const ModuleComponent = definition.component;

              return (
                <div key={placementId} className="dashboard-grid-item min-w-0 h-full">
                  <ErrorBoundary
                    key={`${placementId}:${moduleRetryKeys[placementId] ?? 0}`}
                    fallback={(error) => (
                      <ModuleCard
                        title={definition.title}
                        description={definition.description}
                        icon={definition.icon}
                        moduleColor={placement.moduleColor}
                        onColorChange={(moduleColor) => setModuleColor(placement.placementId, moduleColor)}
                        onRemove={() => removeModule(definition.id)}
                        isAdmin={isAdmin}
                        fillHeight
                        showSpanControl={false}
                      >
                        <div className="space-y-3">
                          <div className="dashboard-alert-error dashboard-body-text rounded-2xl p-3">
                            <p>Module fallback active</p>
                            <p className="dashboard-meta-text mt-1 text-rose-500">
                              {error.message || 'This module failed to render.'}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => retryModule(placementId)}
                              className="dashboard-button-neutral dashboard-button-text flex h-9 items-center rounded-xl px-3"
                            >
                              retry
                            </button>
                            <button
                              type="button"
                              onClick={() => void resetModule(placementId, definition.id)}
                              className="dashboard-button-accent dashboard-button-text flex h-9 items-center rounded-xl px-3"
                            >
                              reset
                            </button>
                          </div>
                        </div>
                      </ModuleCard>
                    )}
                  >
                    <ModuleCard
                      title={definition.title}
                      description={definition.description}
                      icon={definition.icon}
                      moduleColor={placement.moduleColor}
                      onColorChange={(moduleColor) => setModuleColor(placement.placementId, moduleColor)}
                      onRemove={() => removeModule(definition.id)}
                      isAdmin={isAdmin}
                      fillHeight
                      showSpanControl={false}
                    >
                      <ModuleComponent moduleId={definition.id} />
                    </ModuleCard>
                  </ErrorBoundary>
                </div>
              );
            })}
          </ResponsiveGridLayout>
        ) : (
          <div className="dashboard-surface-soft min-h-[240px] rounded-3xl" />
        )}
      </div>

      <TabsDrawer
        isCollapsed={drawerState.isCollapsed}
        searchQuery={searchQuery}
        availableModules={availableModules}
        placedModules={placedModules}
        onToggleCollapsed={handleToggleDrawer}
        onSearchChange={setSearchQuery}
        onAddModule={addModule}
        onRemoveModule={removeModule}
        onResetBoard={resetBoard}
        isAdmin={isAdmin}
      />
    </div>
  );
};

export default DashboardBoard;
